import { getErrorMessageFromResponse } from '../../common/utils/errorUtils';

const FRIENDLY_TOOL_MESSAGES = {
  view: 'Reviewing workspace files…',
  read_file: 'Reviewing workspace files…',
  list_files: 'Inspecting workspace structure…',
  list_dir: 'Inspecting workspace structure…',
  edit: 'Writing application code…',
  write_file: 'Writing application code…',
  searchStockImages: 'Selecting relevant stock imagery…',
  npm_install: 'Configuring project dependencies…',
  verify: 'Running build & verification checks…',
  test: 'Running tests…',
  submit_application_plan: 'Validating application plan…',
  create_collection: 'Setting up project collections…',
  list_collections: 'Reviewing project collections…',
  update_collection: 'Updating project collections…',
  delete_collection: 'Updating project collections…',
};

const PHASE_MESSAGES = {
  blueprint: 'Selecting micro-SaaS foundation…',
  plan: 'Creating application implementation plan…',
  customize: 'Customizing application domain and features…',
  verify: 'Running verification checks…',
  repair: 'Refining code for verification…',
  finalize: 'Finalizing application setup…',
  classifying: 'Understanding your request…',
  generating: 'Building the web application…',
};

export const getPhaseDisplayName = (name) => {
  if (!name) return '';
  return PHASE_MESSAGES[name] || `Working on ${name}…`;
};

export const formatDurationMs = (remainingMs) => {
  if (typeof remainingMs !== 'number') return null;
  const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  return `${mins}m ${secs < 10 ? '0' : ''}${secs}s`;
};

const STOP_REASON_MESSAGES = {
  turn_limit: 'Reached model-turn limit',
  tool_limit: 'Reached tool-call limit',
  deadline: 'Reached the time limit',
  task_turn_limit: 'Reached per-task turn limit',
  user_cancelled: 'Stopped by user',
  connection_lost: 'Connection lost',
};

export const getStopReasonMessage = (reason) => {
  if (!reason) return null;
  return STOP_REASON_MESSAGES[reason] || null;
};

export const getRunEventMessage = (event) => {
  if (!event) return '';

  if (event.log && typeof event.log.message === 'string') return event.log.message;

  const detail = event.detail;
  if (detail) return detail;

  const error = event.error;
  if (error) return error;

  const tool = event.tool;
  if (tool) {
    if (event.type === 'tool_failed') {
      return 'Retrying step…';
    }
    return FRIENDLY_TOOL_MESSAGES[tool] || 'Working on application features…';
  }

  const name = event.name;
  if (name) {
    return getPhaseDisplayName(name);
  }

  const type = event.type;
  return typeof type === 'string' ? type.replace(/_/g, ' ') : '';
};

export const getBuildLogs = (runEvents, errorMessage = '') => {
  const logs = (Array.isArray(runEvents) ? runEvents : [])
    .filter((event) => event && event.loggable !== false)
    .map((event) => {
      const message = getRunEventMessage(event);
      if (!message) return '';
      return {
        message: event.type === 'failed' ? `Error: ${message}` : message,
        level: event.log?.level || (event.type === 'failed' ? 'error' : 'info'),
      };
    })
    .filter((log) => log);

  if (errorMessage) {
    const errorLog = `Error: ${errorMessage}`;
    if (!logs.some((log) => log.message === errorLog)) {
      logs.push({ message: errorLog, level: 'error' });
    }
  }

  return logs;
};

export const getFileLanguage = (filePath) => {
  if (typeof filePath !== 'string' || !filePath) return 'typescript';

  const parts = filePath.split('.');
  const extension = parts.pop();
  const normalizedExtension = typeof extension === 'string' ? extension.toLowerCase() : '';
  const languageByExtension = {
    js: 'javascript',
    jsx: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    css: 'css',
    scss: 'scss',
    html: 'html',
    json: 'json',
    md: 'markdown',
  };
  return languageByExtension[normalizedExtension] || 'plaintext';
};

export const isPngFile = (filePath) => typeof filePath === 'string' && /\.png$/i.test(filePath);

const formatUserErrorMessage = (rawMessage, fallbackMessage) => {
  if (typeof rawMessage !== 'string' || !rawMessage.trim()) return fallbackMessage || '';
  const text = rawMessage.trim();

  if (
    text.indexOf('"code": "invalid_type"') !== -1 ||
    text.indexOf('"path":') !== -1 ||
    text.indexOf('"acceptanceChecks"') !== -1 ||
    (text.charAt(0) === '[' && text.charAt(text.length - 1) === ']')
  ) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed) && parsed.length > 0 && (parsed[0].code || parsed[0].message)) {
        return 'Application plan validation error. Please refine your prompt or try again.';
      }
    } catch (_err) {
      if (text.indexOf('"code":') !== -1) {
        return 'Application plan validation error. Please refine your prompt or try again.';
      }
    }
  }

  if (
    text.indexOf('exceeded the inspection limit') !== -1 ||
    text.indexOf('exceeded the attempt limit') !== -1
  ) {
    return 'The agent could not finalize a plan. Please try again with a clearer prompt.';
  }

  if (text.indexOf('Invalid tool arguments') !== -1) {
    return 'The model response format could not be processed. Please retry or refine your prompt.';
  }

  if (
    /unfinished \(length\)|output token limit|truncated artifact|smaller complete files/i.test(text)
  ) {
    return 'The agent response was too large and was automatically paused. Click Continue building or send another message to resume with smaller file changes.';
  }

  if (text.length > 200) {
    return text.slice(0, 197) + '…';
  }

  return text;
};

export const getShortChatError = (message) => {
  const text = message.replace(/\s+/g, ' ').trim();
  if (/another generation is already editing this project/i.test(text)) {
    return 'Another build is running.';
  }
  return text.length > 90 ? `${text.slice(0, 87).trim()}…` : text;
};

export const getErrorMessage = (error, fallbackMessage) => {
  if (!error) return fallbackMessage;

  const rawFromResponse = getErrorMessageFromResponse(error);
  if (
    rawFromResponse &&
    rawFromResponse !== 'Unknown error occurred' &&
    rawFromResponse !== 'An unknown error occurred'
  ) {
    return formatUserErrorMessage(rawFromResponse, fallbackMessage);
  }

  if (typeof error === 'string') {
    return formatUserErrorMessage(error, fallbackMessage);
  }

  if (typeof error.message === 'string' && error.message) {
    return formatUserErrorMessage(error.message, fallbackMessage);
  }

  return fallbackMessage;
};

export const getFileName = (filePath) => {
  if (typeof filePath !== 'string' || !filePath) return '';
  const parts = filePath.split('/');
  return parts.pop() || filePath;
};

export const buildFileTree = (filesMap) => {
  const sourceFiles = filesMap && typeof filesMap === 'object' ? filesMap : {};
  const paths = Object.keys(sourceFiles).filter(Boolean).sort();

  const root = { name: '', path: '', type: 'folder', children: [] };

  const ensureFolder = (parent, name, fullPath) => {
    let node = parent.children.find((c) => c.type === 'folder' && c.name === name);
    if (!node) {
      node = { name, path: fullPath, type: 'folder', children: [] };
      parent.children.push(node);
    }
    return node;
  };

  paths.forEach((filePath) => {
    const parts = filePath.split('/').filter(Boolean);
    if (!parts.length) return;

    let current = root;
    let acc = '';

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;
      acc = acc ? `${acc}/${part}` : part;

      if (isLast) {
        const exists = current.children.some((c) => c.path === filePath && c.type === 'file');
        if (!exists) {
          current.children.push({
            name: part,
            path: filePath,
            type: 'file',
          });
        }
      } else {
        current = ensureFolder(current, part, acc);
      }
    });
  });

  const sortNodes = (nodes) => {
    nodes.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    nodes.forEach((n) => {
      if (n.children && n.children.length) sortNodes(n.children);
    });
  };

  sortNodes(root.children);
  return root.children;
};

export const getInitialFilePath = (workspace) => {
  const workspaceFiles = workspace && workspace.files;
  const files = Array.isArray(workspaceFiles) ? workspaceFiles : [];
  const appFile = files.find((file) => file && file.path === 'src/App.tsx');
  if (appFile) return appFile.path;

  const componentFile = files.find((file) => {
    const filePath = file && file.path;
    return typeof filePath === 'string' && (filePath.endsWith('.tsx') || filePath.endsWith('.jsx'));
  });
  if (componentFile) return componentFile.path;

  const firstFile = files[0];
  return firstFile && firstFile.path ? firstFile.path : '';
};

export { STATUS_ICONS } from './utils/progressUtils';
export {
  BUTTON_STYLE,
  CARD_STATE_STYLES,
  CARD_STYLE,
  PLACEHOLDER,
  TEXTAREA_STYLE,
} from './utils/promptUtils';
